// src/components/kiosk/AutoRedirectTimer.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AutoRedirectTimer = ({ 
  redirectDelay = 7000, // 7 seconds
  redirectPath = '/kiosk-clock-interface',
  onRedirect,
  className = ''
}) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(Math.ceil(redirectDelay / 1000));
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Use setTimeout to avoid calling navigate during render
          setTimeout(() => {
            setIsActive(false);
            if (onRedirect) {
              onRedirect();
            }
            navigate(redirectPath);
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, navigate, redirectPath, onRedirect]);

  const handleRedirect = () => {
    setIsActive(false);
    if (onRedirect) {
      onRedirect();
    }
    // Use setTimeout to ensure navigation happens after state update
    setTimeout(() => {
      navigate(redirectPath);
    }, 0);
  };

  const handleManualRedirect = () => {
    setIsActive(false);
    handleRedirect();
  };

  const handleStayOnPage = () => {
    setIsActive(false);
    setTimeLeft(0);
  };

  if (!isActive && timeLeft === 0) {
    return null;
  }

  return (
    <div className={`bg-muted/50 border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between">
        {/* Timer Display */}
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon 
              name="Timer" 
              size={20} 
              className="text-primary"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">
              Rückkehr zum Kiosk in {timeLeft} Sekunden
            </p>
            <p className="text-xs text-text-secondary">
              Bildschirm berühren, um auf dieser Seite zu bleiben
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleStayOnPage}
            iconName="Pause"
            iconPosition="left"
            className="touch-feedback"
          >
            Bleiben
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={handleManualRedirect}
            iconName="ArrowRight"
            iconPosition="right"
            className="touch-feedback"
          >
            Weiter
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="w-full bg-border rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-1000 ease-linear"
            style={{ 
              width: `${((Math.ceil(redirectDelay / 1000) - timeLeft) / Math.ceil(redirectDelay / 1000)) * 100}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default AutoRedirectTimer;